import {AuthorityType} from '@sollapay/enums';
import {FieldCombobox, FieldInput, SectionDivider} from '@sollapay/ui/components';
import {useTranslation} from 'react-i18next';

import {AiFlashWrapper} from '@/features/ai-extraction';

import {BankAccountFields} from './BankAccountFields';
import {TaxAuthorityManualPaymentNotice} from './TaxAuthorityManualPaymentNotice';
import {isBankCheckAccountType} from '../utils/creditor-account.utils';

import type {BankAccountFieldsProps} from './BankAccountFields';
import type {PayeeKind} from '../utils/creditor-account.utils';
import type {FieldComboboxOption} from '@sollapay/ui/components';

const PREFIX = 'view.payouts.create.details';

export interface PayeeSpecificFieldsProps extends BankAccountFieldsProps {
  payeeKind: PayeeKind;
  authorityName: string;
  onAuthorityNameChange: (value: string) => void;
  municipalAuthorityOptions: FieldComboboxOption[];
  authorityNameError: string | undefined;
  paymentReference: string;
  onPaymentReferenceChange: (value: string) => void;
  paymentReferenceError: string | undefined;
  voucherNumber: string;
  onVoucherNumberChange: (value: string) => void;
  voucherNumberError: string | undefined;
}

/**
 * Payee-specific fields, hidden until a payee is picked: municipality gets a section divider,
 * the authority-name combobox, the shared bank fields, then a payment reference; RMI gets a
 * divider, the bank fields, and a payment reference (no name); beneficiary gets a divider and
 * the bank fields only; Tax Authority gets a single voucher/reference number instead of any
 * bank field.
 */
export function PayeeSpecificFields({
  payeeKind,
  authorityName,
  onAuthorityNameChange,
  municipalAuthorityOptions,
  authorityNameError,
  paymentReference,
  onPaymentReferenceChange,
  paymentReferenceError,
  voucherNumber,
  onVoucherNumberChange,
  voucherNumberError,
  ...bankAccountFieldsProps
}: PayeeSpecificFieldsProps) {
  const {t} = useTranslation('trustAccounts');
  const isCheck = isBankCheckAccountType(bankAccountFieldsProps.accountType);

  if (payeeKind === 'none') return null;

  if (payeeKind === AuthorityType.TAX_AUTHORITY) {
    return (
      <>
        <TaxAuthorityManualPaymentNotice />
        <AiFlashWrapper
          field="voucherNumber"
          fieldAi={bankAccountFieldsProps.fieldAi}
          isFlashing={bankAccountFieldsProps.isFlashing}
        >
          {ai => (
            <FieldInput
              label={t(`${PREFIX}.voucherNumber`)}
              placeholder={t(`${PREFIX}.voucherNumberPlaceholder`)}
              value={voucherNumber}
              onChange={e => {
                ai.onAiChange();
                onVoucherNumberChange(e.target.value);
              }}
              cornerBadge={ai.aiIndicator}
              isLoading={ai.aiGenerating}
              error={voucherNumberError}
              required
            />
          )}
        </AiFlashWrapper>
      </>
    );
  }

  if (payeeKind === AuthorityType.MUNICIPALITY) {
    return (
      <>
        {!isCheck && (
          <SectionDivider title={t(`${PREFIX}.authorityAccountSectionTitle`)} className="-my-1" />
        )}
        <AiFlashWrapper
          field="authorityName"
          fieldAi={bankAccountFieldsProps.fieldAi}
          isFlashing={bankAccountFieldsProps.isFlashing}
        >
          {ai => (
            <FieldCombobox
              label={t(`${PREFIX}.authorityName`)}
              placeholder={t(`${PREFIX}.authorityNamePlaceholder`)}
              options={municipalAuthorityOptions}
              value={authorityName}
              onValueChange={val => {
                ai.onAiChange();
                onAuthorityNameChange(val);
              }}
              cornerBadge={ai.aiIndicator}
              loading={ai.aiGenerating}
              emptyText={t(`${PREFIX}.authorityNameEmpty`)}
              error={authorityNameError}
              required
            />
          )}
        </AiFlashWrapper>
        <BankAccountFields {...bankAccountFieldsProps} />
        <PaymentReferenceField
          value={paymentReference}
          onChange={onPaymentReferenceChange}
          error={paymentReferenceError}
          isFlashing={bankAccountFieldsProps.isFlashing}
          fieldAi={bankAccountFieldsProps.fieldAi}
        />
      </>
    );
  }

  if (payeeKind === AuthorityType.ISRAEL_LAND_AUTHORITY) {
    return (
      <>
        {!isCheck && (
          <SectionDivider title={t(`${PREFIX}.authorityAccountSectionTitle`)} className="-my-1" />
        )}
        <BankAccountFields {...bankAccountFieldsProps} />
        <PaymentReferenceField
          value={paymentReference}
          onChange={onPaymentReferenceChange}
          error={paymentReferenceError}
          isFlashing={bankAccountFieldsProps.isFlashing}
          fieldAi={bankAccountFieldsProps.fieldAi}
        />
      </>
    );
  }

  if (isCheck) return null;

  return (
    <>
      <SectionDivider title={t(`${PREFIX}.beneficiaryAccountSectionTitle`)} className="-my-1" />
      <BankAccountFields {...bankAccountFieldsProps} />
    </>
  );
}

interface PaymentReferenceFieldProps {
  value: string;
  onChange: (value: string) => void;
  error: string | undefined;
  isFlashing: BankAccountFieldsProps['isFlashing'];
  fieldAi: BankAccountFieldsProps['fieldAi'];
}

/** Shared by municipality and RMI — both payee kinds collect the same payment-reference field. */
function PaymentReferenceField({
  value,
  onChange,
  error,
  isFlashing,
  fieldAi
}: PaymentReferenceFieldProps) {
  const {t} = useTranslation('trustAccounts');

  return (
    <AiFlashWrapper field="paymentReference" fieldAi={fieldAi} isFlashing={isFlashing}>
      {ai => (
        <FieldInput
          label={t(`${PREFIX}.paymentReference`)}
          placeholder={t(`${PREFIX}.paymentReferencePlaceholder`)}
          description={t(`${PREFIX}.paymentReferenceDescription`)}
          value={value}
          onChange={e => {
            ai.onAiChange();
            onChange(e.target.value);
          }}
          cornerBadge={ai.aiIndicator}
          isLoading={ai.aiGenerating}
          error={error}
          required
        />
      )}
    </AiFlashWrapper>
  );
}
